import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { PlayIcon } from '@heroicons/react/24/solid';
import { COLORS, FONTS, BTN_GOLD_STYLE, BTN_OUTLINE_STYLE } from './theme';
import GoldDivider from './GoldDivider';
import InkText from './InkText';
import Shimmer from './Shimmer';
import { trackEvent } from '../../utils/analytics';

const scrollTo = (id: string) =>
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

const EASE = [0.22, 0.61, 0.36, 1] as const;

const PaathshaalaHero: React.FC = () => {
  const reduceMotion = useReducedMotion();

  const rv = (delay: number) => ({
    initial: reduceMotion ? { opacity: 1 } : { opacity: 0, y: 26 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 1, delay, ease: EASE },
  });

  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col items-center justify-center text-center pt-[72px] pb-16"
    >
      <div className="w-full max-w-[980px] mx-auto px-[26px] md:px-10">
        {/* Eyebrow */}
        <motion.p
          {...rv(0)}
          className="text-[13px] uppercase font-semibold tracking-[0.32em] mb-[22px]"
          style={{ fontFamily: FONTS.body, color: COLORS.goldLeafDark }}
        >
          An audio paathshala for the modern family
        </motion.p>

        {/* Masthead — gold-leaf sheen sweeps across once, settles vermillion */}
        <motion.h1
          {...rv(0.1)}
          className="font-bold italic leading-[1.04] tracking-[-0.01em]"
          style={{
            fontFamily: FONTS.heading,
            fontSize: 'clamp(40px, 13.5vw, 96px)',
          }}
        >
          <motion.span
            className="inline-block bg-clip-text"
            style={{
              backgroundImage: `linear-gradient(108deg, ${COLORS.redInk} 0%, ${COLORS.redInk} 40%, #A53A24 46%, #C5913E 50%, #A53A24 54%, ${COLORS.redInk} 60%, ${COLORS.redInk} 100%)`,
              backgroundSize: '320% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
            initial={{ backgroundPosition: '100% 0' }}
            animate={{ backgroundPosition: reduceMotion ? '100% 0' : '0% 0' }}
            transition={{ duration: 3.4, delay: 1, ease: [0.45, 0, 0.2, 1] }}
          >
            Paathshaala
          </motion.span>
        </motion.h1>

        <motion.div {...rv(0.2)} className="mt-[26px] mb-[34px]">
          <GoldDivider draw />
        </motion.div>

        {/* Hook — words ink themselves in */}
        <InkText
          text="Screen time is rewiring everyone’s brains. Attention spans are collapsing. Teen anxiety is at an all-time high."
          baseDelay={300}
          step={55}
          className="font-bold mx-auto mb-5 md:mb-[26px] leading-[1.38] md:leading-[1.3] md:tracking-[-0.012em] max-w-[30ch] md:max-w-none [text-wrap:pretty]"
          style={{
            fontFamily: FONTS.heading,
            color: COLORS.nearBlack,
            fontSize: 'clamp(21px, 5.6vw, 28px)',
          }}
        />

        {/* The second worry */}
        <InkText
          text="And for parents raising kids, there’s a second worry the articles never mention — they’re growing up fluent in everything except their own culture."
          baseDelay={500}
          step={38}
          className="italic mx-auto mb-5 md:mb-[26px] leading-[1.55] max-w-[42ch] md:max-w-[820px] [text-wrap:pretty]"
          style={{
            fontFamily: FONTS.body,
            color: COLORS.redInk,
            fontSize: 'clamp(18px, 4.9vw, 26px)',
          }}
        />

        {/* The parent's lived struggle + gold underline draw */}
        <motion.p
          {...rv(0.54)}
          className="mx-auto mb-9 md:mb-[42px] text-[17px] md:text-[19px] max-w-[46ch] md:max-w-[780px] [text-wrap:pretty]"
          style={{ fontFamily: FONTS.body, color: COLORS.brown }}
        >
          You limit the iPad. You do the weekend paathshala run. And it still
          feels like you&rsquo;re losing to the algorithm &mdash; while your kid
          quietly fights the crushing pressure of school, social media, and a
          racing mind. The disconnect is real.{' '}
          <em
            className="relative inline-block font-semibold not-italic"
            style={{ color: COLORS.darkBrown }}
          >
            <span className="italic">Paathshaala is the bridge.</span>
            <motion.span
              aria-hidden="true"
              className="absolute left-0 right-0 bottom-[-3px] h-[2px] origin-left"
              style={{
                background: `linear-gradient(90deg, ${COLORS.goldLeaf}, ${COLORS.goldLeafDark})`,
              }}
              initial={{ scaleX: reduceMotion ? 1 : 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.3, delay: 1.1, ease: EASE }}
            />
          </em>
        </motion.p>

        {/* CTAs */}
        <motion.div
          {...rv(0.66)}
          className="flex flex-col md:flex-row md:justify-center items-stretch gap-3.5 w-full max-w-[340px] md:max-w-none mx-auto mb-[26px]"
        >
          <button
            onClick={() => {
              trackEvent('paathshala_cta_hero_click', { target: 'register' });
              scrollTo('register');
            }}
            className="relative overflow-hidden inline-flex items-center justify-center gap-2.5 px-[30px] py-3.5 md:min-w-[240px] cursor-pointer transition-transform duration-300 hover:-translate-y-0.5 active:translate-y-0"
            style={BTN_GOLD_STYLE}
          >
            <Shimmer />
            Register for access
          </button>
          <button
            onClick={() => {
              trackEvent('paathshala_cta_hero_click', { target: 'sample' });
              scrollTo('audio-sample');
              window.dispatchEvent(new CustomEvent('paathshaala:listen'));
            }}
            className="inline-flex items-center justify-center gap-2.5 px-[30px] py-3.5 md:min-w-[240px] cursor-pointer transition-colors hover:bg-[#8B1A1A12]"
            style={BTN_OUTLINE_STYLE}
          >
            <PlayIcon className="w-4 h-4 flex-none" />
            Listen to a 2-min sample
          </button>
        </motion.div>

        {/* Microline */}
        <motion.p
          {...rv(0.78)}
          className="text-[13.5px] uppercase tracking-[0.22em] opacity-85"
          style={{ fontFamily: FONTS.body, color: COLORS.brown }}
        >
          Rooted in Jain Agamas
          <span className="px-1.5" style={{ color: COLORS.goldLeafDark }}>
            &#9670;
          </span>
          Brought to life with AI
        </motion.p>

        {/* Scroll cue */}
        <motion.div {...rv(0.9)} className="mt-11 flex justify-center">
          <motion.span
            aria-hidden="true"
            className="block w-2 h-2 rotate-45"
            style={{ backgroundColor: COLORS.goldLeaf }}
            animate={
              reduceMotion
                ? undefined
                : { opacity: [0.35, 1, 0.35], x: [0, 4, 0], y: [0, 4, 0] }
            }
            transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
          />
        </motion.div>
      </div>
    </section>
  );
};

export default PaathshaalaHero;
